import type { DetectionCategory, DetectionRegion, OcrLine, OcrWord, Rect } from '../types'
import { createId } from './id'
import { expandRect, unionRects } from './rect'

interface MatchRule {
  category: DetectionCategory
  label: string
  confidence: number
  pattern: RegExp
  warning?: string
}

const exactRules: MatchRule[] = [
  { category: 'email', label: 'メールアドレス候補', confidence: 0.98, pattern: /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/iu },
  { category: 'postal', label: '郵便番号候補', confidence: 0.98, pattern: /(?:〒\s*)?\d{3}\s*[-ー−‐]?\s*\d{4}/u },
  { category: 'phone', label: '電話番号候補', confidence: 0.95, pattern: /(?:\+81[-\s]?(?:0)?|0)\d{1,4}[-ー−‐\s]?\d{1,4}[-ー−‐\s]?\d{3,4}/u },
  { category: 'identifier', label: 'IPアドレス候補', confidence: 0.9, pattern: /\b(?:\d{1,3}\.){3}\d{1,3}\b/u },
  { category: 'payment', label: 'カード番号候補', confidence: 0.91, pattern: /(?:\d[ -]?){13,19}/u, warning: '数字の並びを確認してください' },
]

const addressTerms = /(北海道|東京都|京都府|大阪府|.{2,3}県|市|区|郡|町|村|丁目|番地|番|号|大字|字)/u
const schoolTerms = /(小学校|中学校|高等学校|高校|大学|短期大学|専門学校|幼稚園|保育園|こども園|学園|学校法人)/u
const nameLabels = /(氏名|お名前|名前|宛名|保護者|受取人|ご依頼主|お届け先|担当者)/u
const idLabels = /(会員番号|注文番号|受注番号|受付番号|予約番号|伝票番号|送り状|お問い合わせ番号|顧客番号|社員番号|学籍番号|契約番号|管理番号|ID|No\.?)/iu
const receiptTerms = /(領収書|レシート|合計|小計|税込|税抜|お釣り|現金|クレジット|登録番号)/u
const honorificName = /^[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}・々ヶ\s]{2,16}(?:様|殿|先生|さん)$/u
const genericJapaneseName = /^[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}・々ヶ\s]{2,12}$/u
const vehiclePattern = /(?:[\p{Script=Han}\p{Script=Hiragana}]{1,5}\s*)?\d{1,3}\s*[\p{Script=Hiragana}]?\s*\d{1,2}[-ー−‐]?\d{2}/u
const numberLike = /\d/u

function normalizedText(value: string): string {
  return value.normalize('NFKC').replace(/[\u200B-\u200D\uFEFF]/g, '').trim()
}

function digitsOnly(value: string): string {
  return normalizedText(value).replace(/\D/g, '')
}

export function luhnValid(value: string): boolean {
  const digits = digitsOnly(value)
  if (digits.length < 13 || digits.length > 19) return false
  let sum = 0
  let doubleDigit = false
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let n = Number(digits[index])
    if (doubleDigit) {
      n *= 2
      if (n > 9) n -= 9
    }
    sum += n
    doubleDigit = !doubleDigit
  }
  return sum % 10 === 0
}

export function isValidJapaneseMyNumber(value: string): boolean {
  const digits = digitsOnly(value)
  if (!/^\d{12}$/.test(digits)) return false
  const check = Number(digits[11])
  let sum = 0
  for (let n = 1; n <= 11; n += 1) {
    const digit = Number(digits[11 - n])
    const weight = n <= 6 ? n + 1 : n - 5
    sum += digit * weight
  }
  const remainder = sum % 11
  const expected = remainder <= 1 ? 0 : 11 - remainder
  return check === expected
}

function wordRect(word: OcrWord): Rect {
  return {
    x: word.bbox.x0,
    y: word.bbox.y0,
    width: Math.max(1, word.bbox.x1 - word.bbox.x0),
    height: Math.max(1, word.bbox.y1 - word.bbox.y0),
  }
}

function lineRect(line: OcrLine): Rect {
  return {
    x: line.bbox.x0,
    y: line.bbox.y0,
    width: Math.max(1, line.bbox.x1 - line.bbox.x0),
    height: Math.max(1, line.bbox.y1 - line.bbox.y0),
  }
}

function makeRegion(
  rect: Rect,
  category: DetectionCategory,
  label: string,
  confidence: number,
  evidence: string,
  imageWidth: number,
  imageHeight: number,
  warning?: string,
): DetectionRegion {
  const padding = Math.max(3, Math.min(rect.height * 0.14, 18))
  return {
    id: createId('ocr'),
    rect: expandRect(rect, padding, imageWidth, imageHeight),
    category,
    source: 'ocr',
    label,
    confidence: Math.max(0, Math.min(1, confidence)),
    enabled: true,
    evidence,
    warning,
  }
}

function lineWithNeighbors(lines: OcrLine[], index: number, count = 1): OcrLine[] {
  return lines.slice(Math.max(0, index - count), Math.min(lines.length, index + count + 1))
}

export function classifyOcrLines(lines: OcrLine[], imageWidth: number, imageHeight: number): DetectionRegion[] {
  const result: DetectionRegion[] = []

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex]
    if (!line) continue
    const text = normalizedText(line.text)
    if (!text) continue
    const confidenceBase = Math.max(0.35, Math.min(1, line.confidence / 100))

    for (const rule of exactRules) {
      const match = text.match(rule.pattern)
      if (!match) continue
      if (rule.category === 'payment' && !luhnValid(match[0])) continue
      const matchingWords = line.words.filter((word) => rule.pattern.test(normalizedText(word.text)))
      const rect = unionRects(matchingWords.map(wordRect)) ?? lineRect(line)
      result.push(makeRegion(rect, rule.category, rule.label, Math.min(rule.confidence, confidenceBase + 0.15), match[0], imageWidth, imageHeight, rule.warning))
    }

    const digits = digitsOnly(text)
    if (isValidJapaneseMyNumber(digits)) {
      result.push(makeRegion(lineRect(line), 'identifier', '個人番号候補', Math.min(0.96, confidenceBase + 0.1), text, imageWidth, imageHeight, '12桁の番号を確認してください'))
    }

    if (schoolTerms.test(text)) {
      result.push(makeRegion(lineRect(line), 'school', '学校・施設名候補', Math.min(0.9, confidenceBase + 0.08), text, imageWidth, imageHeight))
    }

    if (receiptTerms.test(text)) {
      result.push(makeRegion(lineRect(line), 'receipt', 'レシート・領収書情報候補', Math.min(0.72, confidenceBase), text, imageWidth, imageHeight, '金額や店舗情報が必要か確認してください'))
    }

    if (idLabels.test(text) && numberLike.test(text)) {
      result.push(makeRegion(lineRect(line), 'identifier', '会員・注文・管理番号候補', Math.min(0.92, confidenceBase + 0.08), text, imageWidth, imageHeight))
    }

    if (addressTerms.test(text) && (numberLike.test(text) || /(都|道|府|県|市|区|町|村)/u.test(text))) {
      const neighbors = lineWithNeighbors(lines, lineIndex, /〒|郵便/u.test(text) ? 1 : 0)
      const rect = unionRects(neighbors.map(lineRect)) ?? lineRect(line)
      result.push(makeRegion(rect, 'address', '住所候補', Math.min(0.91, confidenceBase + 0.06), neighbors.map((item) => item.text).join(' '), imageWidth, imageHeight))
    }

    if (honorificName.test(text)) {
      result.push(makeRegion(lineRect(line), 'name', '氏名・宛名候補', Math.min(0.9, confidenceBase + 0.08), text, imageWidth, imageHeight))
    } else if (nameLabels.test(text)) {
      const next = lines[lineIndex + 1]
      const candidateText = text.replace(nameLabels, '').replace(/[：:]/g, '').trim()
      if (candidateText && genericJapaneseName.test(candidateText)) {
        result.push(makeRegion(lineRect(line), 'name', '氏名候補', Math.min(0.84, confidenceBase + 0.04), candidateText, imageWidth, imageHeight, '氏名は自動判定が難しいため確認してください'))
      } else if (next && genericJapaneseName.test(normalizedText(next.text))) {
        const rect = unionRects([lineRect(line), lineRect(next)]) ?? lineRect(next)
        result.push(makeRegion(rect, 'name', '氏名候補', Math.min(0.79, Math.max(confidenceBase, next.confidence / 100)), `${text} ${next.text}`, imageWidth, imageHeight, '氏名は自動判定が難しいため確認してください'))
      }
    }

    if (vehiclePattern.test(text) && digits.length >= 4 && digits.length <= 7) {
      result.push(makeRegion(lineRect(line), 'vehicle', '車両ナンバー候補', Math.min(0.68, confidenceBase), text, imageWidth, imageHeight, '低信頼候補です。車両のプレート部分を確認してください'))
    }
  }

  return result
}

export function piiCategoryLabel(category: DetectionCategory): string {
  const labels: Record<DetectionCategory, string> = {
    face: '顔', name: '氏名・宛名', address: '住所', postal: '郵便番号', phone: '電話番号', email: 'メールアドレス',
    school: '学校・施設名', identifier: '会員・注文・管理番号', payment: '支払い情報', vehicle: '車両ナンバー',
    qr: 'QRコード', barcode: 'バーコード', receipt: 'レシート情報', other: 'その他',
  }
  return labels[category]
}
